/**
 * Jain Lexicon terms REST — full CRUD against `{VITE_API_BASE_URL}/jain_lexicon/terms/`:
 *
 * | Action   | HTTP    | Helper                | Headers (`X-Tenant-Id` from `VITE_X_TENANTID`; optional JWT when logged in) |
 * |----------|---------|-----------------------|-----------------------------------------------------------------------------|
 * | List     | GET /   | `fetchTermsRemote`    | No auth required; `page` / `page_size` / `count`; portal uses `?status=approved` when supported |
 * | Read one | GET/:id | `fetchTermByIdRemote` | No auth required                                                           |
 * | Create   | POST /  | `createTermMultipart` | multipart `word`, meanings, `status`, `is_featured`, `is_enabled`, `audio`, … |
 * | Update   | PATCH/:id (`PUT` fallback) | `updateTermMultipart` | same fields as multipart         |
 * | Delete   | DELETE/:id | `deleteTermRemote` | Staff JWT (admin UI)                                      |
 *
 * Create/update must use **POST/PATCH multipart**, not GET with `--form`.
 */

import type { Term } from "@/admin/types";
import { getContributorAccessToken, getStaffAccessToken } from "@/lib/authApi";
import { formatApiErrorBody } from "@/lib/formatApiError";
import { normalizeForCompare } from "@/lib/normalizeText";
import { TERMS_PAGE_SIZES } from "@/lib/termsPageSizes";

export const TERMS_PATH_PREFIX = "/jain_lexicon/terms";

export type TermsJwtScope = "portal" | "staff";

function bearerForScope(scope: TermsJwtScope): string | null {
  return scope === "staff"
    ? getStaffAccessToken()
    : getContributorAccessToken();
}

function jlApiBase(): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return (raw ?? "").replace(/\/$/, "");
}

function jlTenantHeaders(): Record<string, string> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { "X-Tenant-Id": trimmed };
}

/** GET list/detail — public; never send JWT (stale tokens cause 401). */
function termsReadHeaders(): HeadersInit {
  return { ...jlTenantHeaders(), Accept: "application/json" };
}

function authHeaders(
  multipartBody: boolean,
  scope: TermsJwtScope
): HeadersInit {
  const h: Record<string, string> = { ...jlTenantHeaders() };
  const token = bearerForScope(scope);
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  if (!multipartBody) {
    h.Accept = "application/json";
  }
  return h;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Between retries: exponential backoff capped, small jitter against thundering herd. */
function retryDelayMs(attemptIndex: number): number {
  const base = 400 * 2 ** attemptIndex;
  const cap = 8000;
  const jitter = Math.floor(Math.random() * 300);
  return Math.min(cap, base) + jitter;
}

/**
 * GET + parse JSON with retries. Mitigates transient failures such as
 * `net::ERR_HTTP2_PROTOCOL_ERROR` where status may be 200 but the body stream aborts.
 */
async function fetchJsonGetWithRetries(
  url: string,
  headers: HeadersInit
): Promise<
  | { kind: "ok"; raw: unknown }
  | { kind: "http_error"; res: Response }
  | { kind: "fatal"; message: string }
> {
  const maxAttempts = 5;
  let lastMessage = "Terms API request failed";

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const res = await fetch(url, {
        method: "GET",
        headers,
        cache: "no-store",
      });
      if (!res.ok) {
        return { kind: "http_error", res };
      }

      let text: string;
      try {
        text = await res.text();
      } catch (e) {
        const inner = e instanceof Error ? e.message : String(e);
        lastMessage =
          inner === "Failed to fetch"
            ? `Could not read response body (${inner}). Try again shortly; if this persists, the API host or proxy may be dropping the HTTP/2 response stream (consider tuning HTTP/2 or pagination on the server).`
            : `Could not read response body (${inner}). Often caused by a dropped HTTP/2 stream; retrying may help.`;
        await sleep(retryDelayMs(attempt));
        continue;
      }

      const trimmed = text.trim();
      if (!trimmed) {
        lastMessage = "Empty response body from terms API";
        await sleep(retryDelayMs(attempt));
        continue;
      }

      try {
        return { kind: "ok", raw: JSON.parse(trimmed) as unknown };
      } catch {
        lastMessage = `Invalid JSON from terms API (${trimmed.slice(0, 200)}${trimmed.length > 200 ? "…" : ""})`;
        await sleep(retryDelayMs(attempt));
        continue;
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      lastMessage =
        msg === "Failed to fetch"
          ? `${msg}: network error or request blocked (check connection, VPN, CORS, mixed content, or API availability).`
          : msg;
      await sleep(retryDelayMs(attempt));
    }
  }

  return { kind: "fatal", message: lastMessage };
}

export interface TermMultipartFields {
  word: string;
  word_gujarati: string;
  word_devanagari: string;
  word_hindi: string;
  meaning_english: string;
  meaning_hindi: string;
  meaning_gujarati: string;
  pronunciation: string;
  status: string;
  example_usage: string;
  contributor_name: string;
  contributor_email: string;
  is_featured: boolean;
  is_enabled: boolean;
}

/** Map UI `rejected` → API `draft` (Django `Status.TextChoices`). */
function termStatusToApi(status: string | undefined): string | undefined {
  if (status === undefined) {
    return undefined;
  }
  const s = String(status).trim().toLowerCase();
  return s === "rejected" ? "draft" : status;
}

/** Build FormData matching backend field names (`is_featured`, `is_enabled`, optional `audio` file). */
export function appendTermMultipartFields(
  fd: FormData,
  fields: Partial<TermMultipartFields> & { admin_notes?: string }
): void {
  const pairs: [string, string | boolean | undefined][] = [
    ["word", fields.word],
    ["word_gujarati", fields.word_gujarati],
    ["word_devanagari", fields.word_devanagari],
    ["word_hindi", fields.word_hindi],
    ["meaning_english", fields.meaning_english],
    ["meaning_hindi", fields.meaning_hindi],
    ["meaning_gujarati", fields.meaning_gujarati],
    ["pronunciation", fields.pronunciation],
    ["status", termStatusToApi(fields.status)],
    ["example_usage", fields.example_usage],
    ["contributor_name", fields.contributor_name],
    ["contributor_email", fields.contributor_email],
    ["is_featured", fields.is_featured],
    ["is_enabled", fields.is_enabled],
    ["admin_notes", fields.admin_notes],
  ];
  for (const [k, v] of pairs) {
    if (v === undefined) {
      continue;
    }
    fd.append(k, typeof v === "boolean" ? (v ? "true" : "false") : v);
  }
}

export function buildTermFormData(
  fields: Partial<TermMultipartFields> & { admin_notes?: string },
  audioFile?: File | null
): FormData {
  const fd = new FormData();
  appendTermMultipartFields(fd, fields);
  if (audioFile) {
    fd.append("audio", audioFile);
  }
  return fd;
}

export function termToMultipartFields(t: Term): TermMultipartFields {
  return {
    word: t.word,
    word_gujarati: t.word_gujarati ?? "",
    word_devanagari: t.word_devanagari ?? "",
    word_hindi: t.word_hindi ?? "",
    meaning_english: t.meaning_english ?? "",
    meaning_hindi: t.meaning_hindi ?? "",
    meaning_gujarati: t.meaning_gujarati ?? "",
    pronunciation: t.pronunciation ?? "",
    example_usage: t.example_usage ?? "",
    status: t.status,
    contributor_name: t.contributor_name ?? "",
    contributor_email: t.contributor_email ?? "",
    is_featured: t.featured,
    is_enabled: t.is_enabled,
  };
}

async function parseHttpError(res: Response): Promise<string> {
  const fallback = "Something went wrong. Please try again.";
  try {
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const body: unknown = await res.json();
      return formatApiErrorBody(body) ?? fallback;
    }
    const text = await res.text();
    const trimmed = text.trim();
    if (!trimmed) {
      return res.statusText || fallback;
    }
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        const msg = formatApiErrorBody(parsed);
        if (msg) {
          return msg;
        }
      } catch {
        /* plain text */
      }
    }
    return trimmed.length > 400 ? `${trimmed.slice(0, 400)}…` : trimmed;
  } catch {
    return res.statusText || fallback;
  }
}

/** Unwrap single-object JSON: `{ data: {...} }` or plain object. */
function unwrapSingleRecord(
  raw: unknown
): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const d = o.data;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    return d as Record<string, unknown>;
  }
  const result = o.result;
  if (result && typeof result === "object" && !Array.isArray(result)) {
    return result as Record<string, unknown>;
  }
  return o;
}

function coerceList(raw: unknown): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  if (!raw || typeof raw !== "object") {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const fromResults = o.results;
  if (Array.isArray(fromResults)) {
    return fromResults.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  const fromData = o.data;
  if (Array.isArray(fromData)) {
    return fromData.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  const fromContent = o.content;
  if (Array.isArray(fromContent)) {
    return fromContent.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    );
  }
  return [];
}

export type TermsListPagination = {
  count: number;
  page: number;
  page_size: number;
};

function coercePositiveInt(v: unknown, fallback: number): number {
  const n =
    typeof v === "number"
      ? v
      : typeof v === "string"
        ? Number.parseInt(v, 10)
        : NaN;
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

/** Read `count`, `page`, `page_size` from paginated list JSON. */
export function parseTermsListPagination(
  raw: unknown,
  fallback?: { page?: number; page_size?: number }
): TermsListPagination | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const list = coerceList(raw);
  const countRaw = o.count ?? o.total ?? o.total_count;
  const pageSizeFallback = fallback?.page_size ?? (list.length || 1);
  const pageFallback = fallback?.page ?? 1;
  if (countRaw === undefined || countRaw === null) {
    if (list.length === 0) {
      return null;
    }
    return {
      count: list.length,
      page: pageFallback,
      page_size: pageSizeFallback,
    };
  }
  return {
    count: coercePositiveInt(countRaw, list.length),
    page: coercePositiveInt(o.page, pageFallback),
    page_size: coercePositiveInt(o.page_size, pageSizeFallback),
  };
}

function appendTermsQueryParams(
  listRoot: string,
  params: Record<string, string | number | undefined>
): string {
  const url = new URL(
    listRoot,
    listRoot.startsWith("http") ? undefined : "http://local.invalid"
  );
  if (!listRoot.startsWith("http")) {
    const qs = new URLSearchParams();
    const existing = listRoot.split("?")[1];
    if (existing) {
      new URLSearchParams(existing).forEach((v, k) => qs.set(k, v));
    }
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === "") {
        continue;
      }
      qs.set(k, String(v));
    }
    const base = listRoot.split("?")[0];
    const q = qs.toString();
    return q ? `${base}?${q}` : base;
  }
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") {
      continue;
    }
    url.searchParams.set(k, String(v));
  }
  return url.toString();
}

/**
 * Follow-up page URL from paginated list JSON (`next` is common in DRF).
 * Relative paths are resolved using the API base origin.
 */
function termsListNextPageUrl(apiBase: string, raw: unknown): string | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return null;
  }
  const next = (raw as Record<string, unknown>).next;
  if (next === null || next === undefined) {
    return null;
  }
  if (typeof next !== "string") {
    return null;
  }
  const trimmed = next.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  const originMatch = /^(https?:\/\/[^/]+)/i.exec(apiBase);
  const origin = originMatch?.[1];
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (origin) {
    return `${origin}${path}`;
  }
  return `${apiBase.replace(/\/$/, "")}${path}`;
}

/** Public visibility: default true when API omits the field (legacy rows). */
function coerceTermEnabled(r: Record<string, unknown>): boolean {
  const falsy = (v: unknown) =>
    v === false || v === "false" || v === 0 || v === "0";
  const truthy = (v: unknown) =>
    v === true || v === "true" || v === 1 || v === "1";
  for (const key of ["is_enabled", "is_enable", "enabled"] as const) {
    const v = r[key];
    if (v === undefined || v === null) continue;
    if (falsy(v)) return false;
    if (truthy(v)) return true;
  }
  return true;
}

export function mapApiRecordToTerm(r: Record<string, unknown>): Term {
  const pickStr = (...keys: string[]) => {
    for (const k of keys) {
      const v = r[k];
      if (v == null) {
        continue;
      }
      const s = String(v).trim();
      if (s.length) {
        return s;
      }
    }
    return null;
  };
  const rawStatus = (pickStr("status") || "pending").trim().toLowerCase();
  const status: Term["status"] =
    rawStatus === "approved" ||
    rawStatus === "publish" ||
    rawStatus === "published" ||
    rawStatus === "active"
      ? "approved"
      : rawStatus === "rejected" || rawStatus === "draft"
        ? "rejected"
        : "pending";
  const idCandidate = r.id ?? r.uuid ?? r.pk;
  const id =
    idCandidate !== undefined && idCandidate !== null
      ? String(idCandidate).trim()
      : "";

  const nowIso = new Date().toISOString();

  let ownerId = pickStr(
    "user_id",
    "created_by",
    "owner_id",
    "author_id",
    "creator_id"
  );
  if (!ownerId) {
    const u = r.user;
    if (u && typeof u === "object" && !Array.isArray(u)) {
      const id = (u as Record<string, unknown>).id;
      if (id != null) {
        const s = String(id).trim();
        if (s.length) {
          ownerId = s;
        }
      }
    }
  }

  const lc = (v: unknown) =>
    typeof v === "string" ? v.trim().toLowerCase() : "";
  let isAdminAuthored: boolean | undefined;
  if (
    r.created_by_admin === true ||
    r.is_admin_created === true ||
    r.is_staff_created === true ||
    r.is_admin_entry === true ||
    r.is_staff_term === true
  ) {
    isAdminAuthored = true;
  } else if (
    r.created_by_admin === false ||
    r.is_admin_created === false ||
    r.is_staff_created === false
  ) {
    isAdminAuthored = false;
  } else if (
    ["admin", "staff"].includes(lc(r.source)) ||
    ["admin", "staff"].includes(lc(r.entry_type)) ||
    lc(r.submission_source) === "admin" ||
    lc(r.origin) === "staff"
  ) {
    isAdminAuthored = true;
  }

  const contribObj = r.contributor;
  let nestedContributorEmail: string | null = null;
  if (contribObj && typeof contribObj === "object" && !Array.isArray(contribObj)) {
    const co = contribObj as Record<string, unknown>;
    for (const k of ["email", "contributor_email"]) {
      const v = co[k];
      if (typeof v === "string" && v.trim()) {
        nestedContributorEmail = v.trim();
        break;
      }
    }
  }

  return {
    id,
    word: pickStr("word") ?? "",
    word_gujarati: pickStr("word_gujarati"),
    word_devanagari: pickStr("word_devanagari"),
    word_hindi: pickStr("word_hindi"),
    meaning_english: pickStr("meaning_english"),
    meaning_hindi: pickStr("meaning_hindi"),
    meaning_gujarati: pickStr("meaning_gujarati"),
    pronunciation: pickStr("pronunciation"),
    example_usage: pickStr("example_usage"),
    status,
    contributor_email:
      pickStr("contributor_email", "contributorEmail") ?? nestedContributorEmail,
    contributor_name: pickStr("contributor_name", "contributorName"),
    admin_notes: pickStr("admin_notes"),
    user_id: ownerId,
    featured:
      r.is_featured === true ||
      r.is_featured === 1 ||
      r.is_featured === "true" ||
      r.is_featured === "1" ||
      r.featured === true ||
      r.featured === 1 ||
      r.featured === "true" ||
      r.featured === "1",
    /** Rejected terms cannot be public; normalize API quirks that leave visibility true. */
    is_enabled: status === "rejected" ? false : coerceTermEnabled(r),
    ...(isAdminAuthored !== undefined ? { isAdminAuthored } : {}),
    audio_url: pickStr("audio_url", "audio"),
    created_at: pickStr("created_at") ?? nowIso,
    updated_at: pickStr("updated_at") ?? nowIso,
  };
}

export type FetchTermsRemoteOptions = {
  /**
   * Portal JWT only: list every status (`pending` / `approved` / UI `rejected` ↔ API `draft`).
   * Default portal list uses `?status=approved` for public browse; "my contributions"
   * must disable that filter or users only see approved rows.
   */
  portalAllStatuses?: boolean;
  /** 1-based page (sent as `page`). */
  page?: number;
  /** Rows per page (sent as `page_size`). */
  page_size?: number;
  /** Word search (sent as `search` when set). */
  search?: string;
  /** Staff/portal status filter (`pending` / `approved` / `draft` for rejected). */
  status?: string;
  /** When true, loads every page in chunks of `page_size` (default `TERMS_PAGE_SIZES.fetchAllChunk`). */
  fetchAllPages?: boolean;
  /** When true, sends `is_featured=true` (server may filter; client still filters `featured`). */
  is_featured?: boolean;
};

function mergeTermsInto(
  merged: Term[],
  seen: Set<string>,
  list: Record<string, unknown>[]
): void {
  for (const rec of list) {
    const term = mapApiRecordToTerm(rec);
    if (!term.id && !term.word) {
      continue;
    }
    const key = term.id
      ? `id:${term.id}`
      : `w:${normalizeForCompare(term.word)}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(term);
  }
}

function buildTermsListStartUrl(
  listRoot: string,
  scope: TermsJwtScope,
  options?: FetchTermsRemoteOptions
): string {
  const portalApprovedOnly =
    scope === "portal" &&
    !options?.portalAllStatuses &&
    !options?.status;
  const statusParam =
    options?.status?.trim() ||
    (portalApprovedOnly ? "approved" : undefined);

  const page = options?.page ?? 1;
  const page_size =
    options?.page_size ??
    (options?.fetchAllPages
      ? TERMS_PAGE_SIZES.fetchAllChunk
      : TERMS_PAGE_SIZES.browseDefault);

  return appendTermsQueryParams(listRoot, {
    status: statusParam,
    page,
    page_size,
    search: options?.search?.trim() || undefined,
    is_featured: options?.is_featured === true ? "true" : undefined,
  });
}

export async function fetchTermsRemote(
  scope: TermsJwtScope = "portal",
  options?: FetchTermsRemoteOptions
): Promise<{
  terms: Term[];
  error: string | null;
  pagination: TermsListPagination | null;
}> {
  const base = jlApiBase();
  if (!base) {
    return { terms: [], error: "Missing VITE_API_BASE_URL", pagination: null };
  }

  const headers =
    scope === "staff" ? authHeaders(false, "staff") : termsReadHeaders();
  const listRoot = `${base.replace(/\/$/, "")}${TERMS_PATH_PREFIX}/`;
  const portalApprovedOnly =
    scope === "portal" && !options?.portalAllStatuses && !options?.status;
  const startUrl = buildTermsListStartUrl(listRoot, scope, options);

  const merged: Term[] = [];
  const seen = new Set<string>();
  const maxPages = 200;
  let portalRetriedWithoutStatusFilter = false;
  let lastPagination: TermsListPagination | null = null;

  const fetchAllPages = options?.fetchAllPages === true;
  let pageIndex = Math.max(1, options?.page ?? 1);
  const chunkSize =
    options?.page_size ?? TERMS_PAGE_SIZES.fetchAllChunk;

  for (let pages = 0; pages < maxPages; pages += 1) {
    const baseOpts = options ?? {};
    const pageOpts: FetchTermsRemoteOptions = fetchAllPages
      ? {
          ...baseOpts,
          page: pageIndex,
          page_size: chunkSize,
          fetchAllPages: undefined,
        }
      : baseOpts;
    const url = fetchAllPages
      ? buildTermsListStartUrl(listRoot, scope, pageOpts)
      : pages === 0
        ? startUrl
        : null;

    if (!url) {
      break;
    }

    const outcome = await fetchJsonGetWithRetries(url, headers);

    if (outcome.kind === "http_error") {
      const res = outcome.res;
      if (
        scope === "portal" &&
        !portalRetriedWithoutStatusFilter &&
        url === startUrl &&
        portalApprovedOnly
      ) {
        portalRetriedWithoutStatusFilter = true;
        const fallbackUrl = buildTermsListStartUrl(listRoot, scope, {
          ...options,
          status: undefined,
          portalAllStatuses: true,
        });
        const retry = await fetchJsonGetWithRetries(fallbackUrl, headers);
        if (retry.kind === "ok") {
          const raw = retry.raw;
          const list = coerceList(raw);
          mergeTermsInto(merged, seen, list);
          lastPagination = parseTermsListPagination(raw, {
            page: options?.page,
            page_size: options?.page_size ?? chunkSize,
          });
          if (!fetchAllPages) {
            return { terms: merged, error: null, pagination: lastPagination };
          }
          const total = lastPagination?.count ?? merged.length;
          if (merged.length >= total || list.length === 0) {
            return { terms: merged, error: null, pagination: lastPagination };
          }
          pageIndex += 1;
          continue;
        }
      }
      return {
        terms: merged.length > 0 ? merged : [],
        error: await parseHttpError(res),
        pagination: lastPagination,
      };
    }

    if (outcome.kind === "fatal") {
      return {
        terms: merged.length > 0 ? merged : [],
        error: outcome.message,
        pagination: lastPagination,
      };
    }

    const raw = outcome.raw;
    const list = coerceList(raw);
    mergeTermsInto(merged, seen, list);

    lastPagination = parseTermsListPagination(raw, {
      page: fetchAllPages ? pageIndex : options?.page,
      page_size: options?.page_size ?? chunkSize,
    });

    if (!fetchAllPages) {
      return { terms: merged, error: null, pagination: lastPagination };
    }

    const total = lastPagination?.count ?? merged.length;
    if (list.length === 0 || merged.length >= total) {
      return { terms: merged, error: null, pagination: lastPagination };
    }

    if (termsListNextPageUrl(base, raw)) {
      pageIndex += 1;
      continue;
    }

    const pageSize = lastPagination?.page_size ?? chunkSize;
    const currentPage = lastPagination?.page ?? pageIndex;
    if (currentPage * pageSize >= total) {
      return { terms: merged, error: null, pagination: lastPagination };
    }
    pageIndex = currentPage + 1;
  }

  return { terms: merged, error: null, pagination: lastPagination };
}

/** Total row count for a status without loading the full list. */
export async function fetchTermsCountRemote(
  scope: TermsJwtScope,
  status?: string
): Promise<{ count: number; error: string | null }> {
  const { pagination, error } = await fetchTermsRemote(scope, {
    page: 1,
    page_size: TERMS_PAGE_SIZES.countProbe,
    status,
    portalAllStatuses: scope === "portal" && !!status && status !== "approved",
  });
  if (error) {
    return { count: 0, error };
  }
  return { count: pagination?.count ?? 0, error: null };
}

/** GET one term (`/jain_lexicon/terms/{id}/`). */
export async function fetchTermByIdRemote(
  id: string,
  _scope: TermsJwtScope = "staff"
): Promise<{ term: Term | null; error: string | null }> {
  const base = jlApiBase();
  const trimmed = String(id ?? "").trim();
  if (!base) {
    return { term: null, error: "Missing VITE_API_BASE_URL" };
  }
  if (!trimmed) {
    return { term: null, error: "Missing term id" };
  }

  const requestUrl = `${base}${TERMS_PATH_PREFIX}/${encodeURIComponent(trimmed)}/`;
  const outcome = await fetchJsonGetWithRetries(requestUrl, termsReadHeaders());

  if (outcome.kind === "http_error") {
    return { term: null, error: await parseHttpError(outcome.res) };
  }

  if (outcome.kind === "fatal") {
    return { term: null, error: outcome.message };
  }

  const raw = outcome.raw;

  const rec = unwrapSingleRecord(raw);
  if (!rec) {
    return { term: null, error: "Unexpected response shape" };
  }

  const term = mapApiRecordToTerm(rec);
  if (!term.id && term.word === "") {
    return { term: null, error: "Term payload missing id and word" };
  }
  return { term, error: null };
}

function normalizeIdCompare(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase();
}

function normalizeEmailCompare(s: string | null | undefined): string | null {
  const t = (s ?? "").trim().toLowerCase();
  return t.length ? t : null;
}

function collectMatchEmails(
  primary: string | null | undefined,
  extras?: string[] | null
): Set<string> {
  const emailSet = new Set<string>();
  const add = (s: string | null | undefined) => {
    const n = normalizeEmailCompare(s);
    if (n) {
      emailSet.add(n);
    }
  };
  add(primary);
  for (const e of extras ?? []) {
    add(e);
  }
  return emailSet;
}

/** GET terms (all statuses with portal JWT) then keep rows owned by this user (`user_id` or `contributor_email`). */
export async function fetchMyContributionsRemote(options: {
  userId: string;
  /** Primary email compared to API `contributor_email` (case-insensitive). */
  userEmail?: string | null;
  /** Extra addresses to treat as the same user (profile vs JWT vs session). Any match shows the row. */
  matchEmails?: string[] | null;
}): Promise<{ terms: Term[]; error: string | null }> {
  const { terms: all, error } = await fetchTermsRemote("portal", {
    portalAllStatuses: true,
    fetchAllPages: true,
  });
  if (error) {
    return { terms: [], error };
  }

  const wantId = normalizeIdCompare(options.userId);
  const emailSet = collectMatchEmails(options.userEmail, options.matchEmails);

  const mine = all.filter(t => {
    const rowUser = normalizeIdCompare(t.user_id);
    if (wantId && rowUser && rowUser === wantId) {
      return true;
    }
    const rowEmail = normalizeEmailCompare(t.contributor_email);
    if (rowEmail && emailSet.size > 0 && emailSet.has(rowEmail)) {
      return true;
    }
    return false;
  });

  mine.sort((a, b) => {
    const ta = Date.parse(a.created_at) || 0;
    const tb = Date.parse(b.created_at) || 0;
    return tb - ta;
  });

  return { terms: mine, error: null };
}

export type TermWriteResult = {
  ok: boolean;
  error: string | null;
  /** Populated when the API returns the created row as JSON (optional). */
  term?: Term;
};

export async function createTermMultipart(
  fields: Partial<TermMultipartFields> & { admin_notes?: string },
  audioFile?: File | null,
  scope: TermsJwtScope = "portal"
): Promise<TermWriteResult> {
  const base = jlApiBase();
  if (!base) {
    return { ok: false, error: "Missing VITE_API_BASE_URL" };
  }

  const fd = buildTermFormData(fields, audioFile);
  const res = await fetch(`${base}${TERMS_PATH_PREFIX}/`, {
    method: "POST",
    headers: authHeaders(true, scope),
    body: fd,
  });

  if (!res.ok) {
    return { ok: false, error: await parseHttpError(res) };
  }

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    return { ok: true, error: null };
  }
  try {
    const raw: unknown = await res.json();
    const rec = unwrapSingleRecord(raw);
    if (rec) {
      const term = mapApiRecordToTerm(rec);
      if (term.id || term.word) {
        return { ok: true, error: null, term };
      }
    }
  } catch {
    /* ignore */
  }
  return { ok: true, error: null };
}

export async function updateTermMultipart(
  id: string,
  fields: Partial<TermMultipartFields> & { admin_notes?: string },
  audioFile?: File | null,
  scope: TermsJwtScope = "staff"
): Promise<TermWriteResult> {
  const base = jlApiBase();
  if (!base) {
    return { ok: false, error: "Missing VITE_API_BASE_URL" };
  }

  const fd = buildTermFormData(fields, audioFile);
  const encoded = encodeURIComponent(id);
  let res = await fetch(`${base}${TERMS_PATH_PREFIX}/${encoded}/`, {
    method: "PATCH",
    headers: authHeaders(true, scope),
    body: fd,
  });

  if (res.status === 404 || res.status === 405) {
    res = await fetch(`${base}${TERMS_PATH_PREFIX}/${encoded}/`, {
      method: "PUT",
      headers: authHeaders(true, scope),
      body: fd,
    });
  }

  if (!res.ok) {
    return { ok: false, error: await parseHttpError(res) };
  }

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) {
    return { ok: true, error: null };
  }
  try {
    const raw: unknown = await res.json();
    const rec = unwrapSingleRecord(raw);
    if (rec) {
      const term = mapApiRecordToTerm(rec);
      if (term.id || term.word) {
        return { ok: true, error: null, term };
      }
    }
  } catch {
    /* ignore */
  }
  return { ok: true, error: null };
}

export async function deleteTermRemote(
  id: string,
  scope: TermsJwtScope = "staff"
): Promise<{ ok: boolean; error: string | null }> {
  const base = jlApiBase();
  if (!base) {
    return { ok: false, error: "Missing VITE_API_BASE_URL" };
  }

  const res = await fetch(
    `${base}${TERMS_PATH_PREFIX}/${encodeURIComponent(id)}/`,
    {
      method: "DELETE",
      headers: authHeaders(false, scope),
    }
  );

  if (!res.ok && res.status !== 204) {
    return { ok: false, error: await parseHttpError(res) };
  }
  return { ok: true, error: null };
}
